import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { loyaltyService } from '../../services/loyaltyService';
import { vetrineService } from '../../services/vetrineService';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [storefronts, setStorefronts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [processing, setProcessing] = useState(false);

  // Top-up Modal State
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [topUpForm, setTopUpForm] = useState({
    businessId: '',
    eurosReceived: '',
    pointsToLoad: '',
    transferDate: format(new Date(), 'yyyy-MM-dd'),
    transferReference: '',
    notes: ''
  });

  // Payout Modal State
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedBusinessForPayout, setSelectedBusinessForPayout] = useState(null);
  const [payoutForm, setPayoutForm] = useState({
    amountEuro: '',
    paymentDate: format(new Date(), 'yyyy-MM-dd'),
    paymentReference: '',
    notes: ''
  });

  // Ledger Filters
  const [ledgerFilters, setLedgerFilters] = useState({
    businessId: '',
    customerId: '',
    transactionType: '',
    startDate: '',
    endDate: ''
  });

  // Monthly Report State
  const [reportBusinessId, setReportBusinessId] = useState('');
  const [reportMonth, setReportMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [monthlyReport, setMonthlyReport] = useState(null);

  // Reversal Modal State
  const [showReversalModal, setShowReversalModal] = useState(false);
  const [reversalTransaction, setReversalTransaction] = useState(null);
  const [reversalReason, setReversalReason] = useState('');

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      setLoading(true);
      const [businessesData, transactionsData, storefrontsData] = await Promise.all([
        loyaltyService?.getAllBusinesses(),
        loyaltyService?.getLedgerAudit({}),
        vetrineService?.getAllStores()
      ]);
      setBusinesses(businessesData);
      setTransactions(transactionsData);
      setStorefronts(storefrontsData || []);
    } catch (err) {
      setError(err?.message || 'Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleTopUpChange = (field, value) => {
    const newForm = { ...topUpForm, [field]: value };
    
    // Auto-calculate points when euros received changes
    if (field === 'eurosReceived') {
      const euros = parseFloat(value) || 0;
      newForm.pointsToLoad = (euros / 0.5)?.toFixed(2);
    }
    
    setTopUpForm(newForm);
  };

  const handleTopUpSubmit = async () => {
    if (!topUpForm?.businessId || !topUpForm?.eurosReceived || !topUpForm?.transferReference || !topUpForm?.notes) {
      setError('Tutti i campi obbligatori devono essere compilati');
      return;
    }

    try {
      setProcessing(true);
      const points = parseFloat(topUpForm?.pointsToLoad);
      const notes = `Bonifico ricevuto: €${topUpForm?.eurosReceived} - Rif: ${topUpForm?.transferReference} - Data: ${topUpForm?.transferDate} - ${topUpForm?.notes}`;
      
      await loyaltyService?.topUpBusiness(topUpForm?.businessId, points, notes);
      setSuccess('Ricarica negozio effettuata con successo');
      setShowTopUpModal(false);
      resetTopUpForm();
      await loadAdminData();
    } catch (err) {
      setError(err?.message || 'Errore nella ricarica');
    } finally {
      setProcessing(false);
    }
  };

  const resetTopUpForm = () => {
    setTopUpForm({
      businessId: '',
      eurosReceived: '',
      pointsToLoad: '',
      transferDate: format(new Date(), 'yyyy-MM-dd'),
      transferReference: '',
      notes: ''
    });
  };

  const handlePayoutSubmit = async () => {
    if (!payoutForm?.amountEuro || !payoutForm?.paymentReference || !payoutForm?.notes) {
      setError('Tutti i campi obbligatori devono essere compilati');
      return;
    }

    try {
      setProcessing(true);
      const notes = `Pagamento effettuato: €${payoutForm?.amountEuro} - Rif: ${payoutForm?.paymentReference} - Data: ${payoutForm?.paymentDate} - ${payoutForm?.notes}`;
      
      await loyaltyService?.processPayoutManual(
        selectedBusinessForPayout?.id,
        parseFloat(payoutForm?.amountEuro),
        notes
      );
      
      setSuccess('Pagamento registrato con successo');
      setShowPayoutModal(false);
      setSelectedBusinessForPayout(null);
      resetPayoutForm();
      await loadAdminData();
    } catch (err) {
      setError(err?.message || 'Errore nella registrazione del pagamento');
    } finally {
      setProcessing(false);
    }
  };

  const resetPayoutForm = () => {
    setPayoutForm({
      amountEuro: '',
      paymentDate: format(new Date(), 'yyyy-MM-dd'),
      paymentReference: '',
      notes: ''
    });
  };

  const handleGenerateMonthlyReport = async () => {
    if (!reportBusinessId || !reportMonth) {
      setError('Seleziona negozio e mese per il report');
      return;
    }

    try {
      setProcessing(true);
      const report = await loyaltyService?.getMonthlyReport(reportBusinessId, reportMonth);
      setMonthlyReport(report);
    } catch (err) {
      setError(err?.message || 'Errore nella generazione del report');
    } finally {
      setProcessing(false);
    }
  };

  const handleReversalSubmit = async () => {
    if (!reversalReason?.trim()) {
      setError('La motivazione dello storno è obbligatoria');
      return;
    }

    try {
      setProcessing(true);
      await loyaltyService?.reversalTransaction(reversalTransaction?.id, reversalReason);
      setSuccess('Storno effettuato con successo');
      setShowReversalModal(false);
      setReversalTransaction(null);
      setReversalReason('');
      await loadAdminData();
    } catch (err) {
      setError(err?.message || 'Errore nello storno');
    } finally {
      setProcessing(false);
    }
  };

  const applyLedgerFilters = async () => {
    try {
      setLoading(true);
      const filtered = await loyaltyService?.getLedgerAudit(ledgerFilters);
      setTransactions(filtered);
    } catch (err) {
      setError(err?.message || 'Errore nel filtraggio');
    } finally {
      setLoading(false);
    }
  };

  const getTransactionTypeLabel = (type) => {
    switch (type) {
      case 'top_up_negozio':
        return 'Ricarica Negozio';
      case 'carico_punti_cliente':
        return 'Cashback Cliente';
      case 'scarico_punti_cliente':
        return 'Pagamento Cliente';
      case 'payout_negozio':
        return 'Rimborso Negozio';
      case 'reversal':
        return 'Storno';
      default:
        return type;
    }
  };

  const getTotalStats = () => {
    const totalPointsInCirculation = businesses?.reduce((sum, b) => sum + b?.pointsBalance, 0);
    const totalCreditsOwed = businesses?.reduce((sum, b) => sum + b?.creditsOwed, 0);
    const totalPurchased = businesses?.reduce((sum, b) => sum + b?.totalPurchased, 0);
    return { totalPointsInCirculation, totalCreditsOwed, totalPurchased };
  };

  const stats = getTotalStats();

  if (loading && activeTab === 'overview') {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 pb-20 px-6">
          <div className="max-w-7xl mx-auto flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Admin Dashboard - Cliccami.net</title>
      </Helmet>
      <Header />
      <div className="pt-24 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header with Title and Add Button */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600 mt-2">Gestione vetrine e sistema Cliccami</p>
            </div>
            <Button
              onClick={() => navigate('/admin-storefront-form')}
              className="flex items-center gap-2 bg-primary hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              <Icon name="plus" className="w-5 h-5" />
              Aggiungi Vetrina
            </Button>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <Icon name="alert-circle" className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-800">{error}</p>
              </div>
              <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
                <Icon name="x" className="w-5 h-5" />
              </button>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <Icon name="check-circle" className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-green-800">{success}</p>
              </div>
              <button onClick={() => setSuccess('')} className="text-green-600 hover:text-green-800">
                <Icon name="x" className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Stats Cards - Vetrine Focus */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <Icon name="store" className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Vetrine Totali</p>
                  <p className="text-3xl font-bold text-gray-900">{storefronts?.length || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-50 rounded-lg">
                  <Icon name="check-circle" className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Vetrine Attive</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {storefronts?.filter(s => s?.status === 'attiva')?.length || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <Icon name="edit" className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Bozze</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {storefronts?.filter(s => s?.status === 'bozza')?.length || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-4 mb-6 border-b border-border overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`pb-3 px-4 font-medium transition-smooth whitespace-nowrap ${
                activeTab === 'overview' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon name="LayoutDashboard" size={20} />
                <span>Panoramica</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('topup')}
              className={`pb-3 px-4 font-medium transition-smooth whitespace-nowrap ${
                activeTab === 'topup' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon name="ArrowUpCircle" size={20} />
                <span>Ricariche Negozi</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('payouts')}
              className={`pb-3 px-4 font-medium transition-smooth whitespace-nowrap ${
                activeTab === 'payouts' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon name="Banknote" size={20} />
                <span>Liquidazioni</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('ledger')}
              className={`pb-3 px-4 font-medium transition-smooth whitespace-nowrap ${
                activeTab === 'ledger' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon name="FileText" size={20} />
                <span>Ledger Completo</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('reports')}
              className={`pb-3 px-4 font-medium transition-smooth whitespace-nowrap ${
                activeTab === 'reports' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon name="BarChart3" size={20} />
                <span>Report Mensili</span>
              </div>
            </button>
          </div>

          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="bg-card rounded-xl p-6 border border-border shadow-warm">
                <h2 className="text-2xl font-bold text-foreground mb-4">Negozi Attivi</h2>
                <div className="space-y-4">
                  {businesses?.map((business) => (
                    <div key={business?.id} className="bg-background rounded-lg p-4 border border-border">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-foreground mb-2">{business?.businessName}</h3>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <p className="text-sm text-muted-foreground">Saldo Punti</p>
                              <p className="text-lg font-bold text-primary">{business?.pointsBalance?.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground">Totale Acquistato</p>
                              <p className="text-lg font-bold text-foreground">{business?.totalPurchased?.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground">Crediti Dovuti</p>
                              <p className="text-lg font-bold text-warning">€{business?.creditsOwed?.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground">Stato</p>
                              <p className={`text-lg font-bold ${
                                business?.isActive ? 'text-success' : 'text-destructive'
                              }`}>
                                {business?.isActive ? 'Attivo' : 'Inattivo'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'topup' && (
            <div className="space-y-6">
              <div className="bg-card rounded-xl p-6 border border-border shadow-warm">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-foreground">Ricariche Negozi (Top-up)</h2>
                  <Button
                    variant="default"
                    iconName="Plus"
                    onClick={() => setShowTopUpModal(true)}
                  >
                    Nuova Ricarica
                  </Button>
                </div>
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Icon name="Info" size={20} className="text-primary mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-foreground mb-1">Procedura Ricarica</p>
                      <p className="text-sm text-muted-foreground">
                        Le ricariche avvengono solo dopo bonifico manuale e conferma dell'admin. 
                        1 punto = €0,50. Inserire importo ricevuto, riferimento bonifico e nota obbligatoria.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  {transactions?.filter(t => t?.transactionType === 'top_up_negozio')?.slice(0, 10)?.map((tx) => (
                    <div key={tx?.id} className="bg-background rounded-lg p-4 border border-border">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="px-3 py-1 bg-success/10 text-success text-xs font-semibold rounded-full">
                              {getTransactionTypeLabel(tx?.transactionType)}
                            </span>
                            <span className="text-sm text-muted-foreground">
                              {format(new Date(tx?.createdAt), 'dd MMMM yyyy, HH:mm', { locale: it })}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                            <div>
                              <p className="text-sm text-muted-foreground">Negozio</p>
                              <p className="text-sm font-semibold text-foreground">{tx?.businessName}</p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground">Punti Caricati</p>
                              <p className="text-sm font-semibold text-foreground">{tx?.pointsAmount?.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground">Valore</p>
                              <p className="text-sm font-semibold text-foreground">€{tx?.euroAmount?.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground">Saldo Dopo</p>
                              <p className="text-sm font-semibold text-primary">{tx?.businessBalanceAfter?.toFixed(2)}</p>
                            </div>
                          </div>
                          {tx?.notes && (
                            <div className="mt-3">
                              <p className="text-sm text-muted-foreground">Note</p>
                              <p className="text-sm text-foreground">{tx?.notes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'payouts' && (
            <div className="space-y-6">
              <div className="bg-card rounded-xl p-6 border border-border shadow-warm">
                <h2 className="text-2xl font-bold text-foreground mb-6">Liquidazioni Negozi (Payout)</h2>
                <div className="bg-warning/5 border border-warning/20 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-foreground mb-1">Crediti Maturati</p>
                      <p className="text-sm text-muted-foreground">
                        Quando un negozio accetta punti dai clienti, matura un credito in euro (punti x 0,50). 
                        Registra qui i pagamenti effettuati tramite bonifico per scalare il saldo.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  {businesses?.filter(b => b?.creditsOwed > 0)?.map((business) => (
                    <div key={business?.id} className="bg-background rounded-lg p-4 border border-border">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-foreground mb-2">{business?.businessName}</h3>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <div>
                              <p className="text-sm text-muted-foreground">Credito Maturato</p>
                              <p className="text-2xl font-bold text-warning">€{business?.creditsOwed?.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground">Punti Accettati</p>
                              <p className="text-lg font-semibold text-foreground">{(business?.creditsOwed / 0.5)?.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground">Saldo Punti Attuale</p>
                              <p className="text-lg font-semibold text-primary">{business?.pointsBalance?.toFixed(2)}</p>
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="default"
                          size="sm"
                          iconName="Banknote"
                          onClick={() => {
                            setSelectedBusinessForPayout(business);
                            setPayoutForm({ ...payoutForm, amountEuro: business?.creditsOwed?.toFixed(2) });
                            setShowPayoutModal(true);
                          }}
                        >
                          Registra Pagamento
                        </Button>
                      </div>
                    </div>
                  ))}
                  {businesses?.filter(b => b?.creditsOwed > 0)?.length === 0 && (
                    <div className="text-center py-12">
                      <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-4" />
                      <p className="text-muted-foreground">Nessun credito da rimborsare al momento</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'ledger' && (
            <div className="space-y-6">
              <div className="bg-card rounded-xl p-6 border border-border shadow-warm">
                <h2 className="text-2xl font-bold text-foreground mb-6">Ledger Movimenti Completo</h2>
                
                <div className="bg-background rounded-lg p-4 border border-border mb-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Filtri</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Select
                      label="Negozio"
                      value={ledgerFilters?.businessId}
                      onChange={(e) => setLedgerFilters({ ...ledgerFilters, businessId: e?.target?.value })}
                    >
                      <option value="">Tutti i negozi</option>
                      {businesses?.map((b) => (
                        <option key={b?.id} value={b?.id}>{b?.businessName}</option>
                      ))}
                    </Select>
                    
                    <Select
                      label="Tipo Transazione"
                      value={ledgerFilters?.transactionType}
                      onChange={(e) => setLedgerFilters({ ...ledgerFilters, transactionType: e?.target?.value })}
                    >
                      <option value="">Tutti i tipi</option>
                      <option value="top_up_negozio">Ricarica Negozio</option>
                      <option value="carico_punti_cliente">Carico Cliente</option>
                      <option value="scarico_punti_cliente">Scarico Cliente</option>
                      <option value="payout_negozio">Payout Negozio</option>
                      <option value="reversal">Storno</option>
                    </Select>
                    
                    <div className="flex gap-2">
                      <Input
                        type="date"
                        label="Data Inizio"
                        value={ledgerFilters?.startDate}
                        onChange={(e) => setLedgerFilters({ ...ledgerFilters, startDate: e?.target?.value })}
                      />
                      <Input
                        type="date"
                        label="Data Fine"
                        value={ledgerFilters?.endDate}
                        onChange={(e) => setLedgerFilters({ ...ledgerFilters, endDate: e?.target?.value })}
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-4">
                    <Button
                      variant="default"
                      size="sm"
                      iconName="Filter"
                      onClick={applyLedgerFilters}
                    >
                      Applica Filtri
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="X"
                      onClick={() => {
                        setLedgerFilters({
                          businessId: '',
                          customerId: '',
                          transactionType: '',
                          startDate: '',
                          endDate: ''
                        });
                        loadAdminData();
                      }}
                    >
                      Resetta
                    </Button>
                  </div>
                </div>

                <div className="space-y-4">
                  {transactions?.map((tx) => (
                    <div key={tx?.id} className="bg-background rounded-lg p-4 border border-border">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                              tx?.transactionType === 'top_up_negozio' ? 'bg-success/10 text-success' :
                              tx?.transactionType === 'carico_punti_cliente' ? 'bg-primary/10 text-primary' :
                              tx?.transactionType === 'scarico_punti_cliente' ? 'bg-warning/10 text-warning' :
                              tx?.transactionType === 'payout_negozio'? 'bg-info/10 text-info' : 'bg-destructive/10 text-destructive'
                            }`}>
                              {getTransactionTypeLabel(tx?.transactionType)}
                            </span>
                            <span className="text-sm text-muted-foreground">
                              {format(new Date(tx?.createdAt), 'dd MMMM yyyy, HH:mm', { locale: it })}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-3">
                            {tx?.businessName && (
                              <div>
                                <p className="text-sm text-muted-foreground">Negozio</p>
                                <p className="text-sm font-semibold text-foreground">{tx?.businessName}</p>
                              </div>
                            )}
                            {tx?.customerName && (
                              <div>
                                <p className="text-sm text-muted-foreground">Cliente</p>
                                <p className="text-sm font-semibold text-foreground">{tx?.customerName}</p>
                              </div>
                            )}
                            <div>
                              <p className="text-sm text-muted-foreground">Punti</p>
                              <p className="text-sm font-semibold text-foreground">{tx?.pointsAmount?.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground">Valore</p>
                              <p className="text-sm font-semibold text-foreground">€{tx?.euroAmount?.toFixed(2)}</p>
                            </div>
                            {tx?.businessBalanceBefore !== null && (
                              <div>
                                <p className="text-sm text-muted-foreground">Saldo Prima/Dopo</p>
                                <p className="text-sm font-semibold text-foreground">
                                  {tx?.businessBalanceBefore?.toFixed(2)} → {tx?.businessBalanceAfter?.toFixed(2)}
                                </p>
                              </div>
                            )}
                          </div>
                          {tx?.notes && (
                            <div className="mt-3">
                              <p className="text-sm text-muted-foreground">Note</p>
                              <p className="text-sm text-foreground">{tx?.notes}</p>
                            </div>
                          )}
                        </div>
                        {tx?.transactionType !== 'reversal' && (
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="RotateCcw"
                            onClick={() => {
                              setReversalTransaction(tx);
                              setShowReversalModal(true);
                            }}
                          >
                            Storno
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'reports' && (
            <div className="space-y-6">
              <div className="bg-card rounded-xl p-6 border border-border shadow-warm">
                <h2 className="text-2xl font-bold text-foreground mb-6">Report Mensile per Negozio</h2>
                
                <div className="bg-background rounded-lg p-4 border border-border mb-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Genera Report</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Select
                      label="Negozio"
                      value={reportBusinessId}
                      onChange={(e) => setReportBusinessId(e?.target?.value)}
                    >
                      <option value="">Seleziona negozio</option>
                      {businesses?.map((b) => (
                        <option key={b?.id} value={b?.id}>{b?.businessName}</option>
                      ))}
                    </Select>
                    
                    <Input
                      type="month"
                      label="Mese"
                      value={reportMonth}
                      onChange={(e) => setReportMonth(e?.target?.value)}
                    />
                    
                    <div className="flex items-end">
                      <Button
                        variant="default"
                        iconName="BarChart3"
                        onClick={handleGenerateMonthlyReport}
                        loading={processing}
                        fullWidth
                      >
                        Genera Report
                      </Button>
                    </div>
                  </div>
                </div>

                {monthlyReport && (
                  <div className="bg-background rounded-lg p-6 border border-border">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-bold text-foreground">
                        Report {monthlyReport?.businessName} - {format(new Date(reportMonth + '-01'), 'MMMM yyyy', { locale: it })}
                      </h3>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                      <div className="bg-card rounded-lg p-4 border border-border">
                        <p className="text-sm text-muted-foreground mb-2">Punti Acquistati</p>
                        <p className="text-2xl font-bold text-success">{monthlyReport?.pointsPurchased?.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground mt-1">€{(monthlyReport?.pointsPurchased * 0.5)?.toFixed(2)}</p>
                      </div>
                      
                      <div className="bg-card rounded-lg p-4 border border-border">
                        <p className="text-sm text-muted-foreground mb-2">Punti Emessi</p>
                        <p className="text-2xl font-bold text-primary">{monthlyReport?.pointsIssued?.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground mt-1">Cashback clienti</p>
                      </div>
                      
                      <div className="bg-card rounded-lg p-4 border border-border">
                        <p className="text-sm text-muted-foreground mb-2">Punti Riscossi</p>
                        <p className="text-2xl font-bold text-warning">{monthlyReport?.pointsRedeemed?.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground mt-1">Pagamenti clienti</p>
                      </div>
                      
                      <div className="bg-card rounded-lg p-4 border border-border">
                        <p className="text-sm text-muted-foreground mb-2">Saldo Punti</p>
                        <p className="text-2xl font-bold text-foreground">{monthlyReport?.pointsBalance?.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground mt-1">Fine mese</p>
                      </div>
                      
                      <div className="bg-card rounded-lg p-4 border border-border">
                        <p className="text-sm text-muted-foreground mb-2">Euro da Pagare</p>
                        <p className="text-2xl font-bold text-warning">€{monthlyReport?.eurosToPay?.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground mt-1">Crediti maturati</p>
                      </div>
                      
                      <div className="bg-card rounded-lg p-4 border border-border">
                        <p className="text-sm text-muted-foreground mb-2">Euro Pagati</p>
                        <p className="text-2xl font-bold text-success">€{monthlyReport?.eurosPaid?.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground mt-1">Liquidazioni effettuate</p>
                      </div>
                      
                      <div className="bg-card rounded-lg p-4 border border-border">
                        <p className="text-sm text-muted-foreground mb-2">Saldo Euro</p>
                        <p className="text-2xl font-bold text-info">€{monthlyReport?.eurosBalance?.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground mt-1">Da liquidare</p>
                      </div>
                      
                      <div className="bg-card rounded-lg p-4 border border-border">
                        <p className="text-sm text-muted-foreground mb-2">Transazioni</p>
                        <p className="text-2xl font-bold text-foreground">{monthlyReport?.transactionCount}</p>
                        <p className="text-xs text-muted-foreground mt-1">Totale operazioni</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showTopUpModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1100 p-6">
          <div className="bg-card rounded-xl p-6 max-w-2xl w-full border border-border shadow-warm-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-foreground">Nuova Ricarica Negozio</h3>
              <button onClick={() => { setShowTopUpModal(false); resetTopUpForm(); }}>
                <Icon name="X" size={24} className="text-muted-foreground hover:text-foreground" />
              </button>
            </div>

            <div className="space-y-4">
              <Select
                label="Negozio *"
                value={topUpForm?.businessId}
                onChange={(e) => handleTopUpChange('businessId', e?.target?.value)}
              >
                <option value="">Seleziona negozio</option>
                {businesses?.map((b) => (
                  <option key={b?.id} value={b?.id}>{b?.businessName}</option>
                ))}
              </Select>
              
              <Input
                type="number"
                label="Euro Ricevuti (Bonifico) *"
                placeholder="0.00"
                value={topUpForm?.eurosReceived}
                onChange={(e) => handleTopUpChange('eurosReceived', e?.target?.value)}
                step="0.01"
                min="0"
              />
              
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <p className="text-sm font-semibold text-foreground mb-1">Punti da Caricare (Auto-calcolo)</p>
                <p className="text-3xl font-bold text-primary">{topUpForm?.pointsToLoad || '0.00'}</p>
                <p className="text-xs text-muted-foreground mt-1">1 punto = €0,50</p>
              </div>
              
              <Input
                type="date"
                label="Data Bonifico *"
                value={topUpForm?.transferDate}
                onChange={(e) => handleTopUpChange('transferDate', e?.target?.value)}
              />
              
              <Input
                type="text"
                label="Riferimento Bonifico *"
                placeholder="Es: TRN123456789"
                value={topUpForm?.transferReference}
                onChange={(e) => handleTopUpChange('transferReference', e?.target?.value)}
              />
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Nota *</label>
                <textarea
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  rows="3"
                  placeholder="Inserisci dettagli della ricarica..."
                  value={topUpForm?.notes}
                  onChange={(e) => handleTopUpChange('notes', e?.target?.value)}
                />
              </div>
            </div>

            <div className="flex gap-4 mt-6">
              <Button
                variant="outline"
                onClick={() => { setShowTopUpModal(false); resetTopUpForm(); }}
                fullWidth
              >
                Annulla
              </Button>
              <Button
                variant="default"
                onClick={handleTopUpSubmit}
                loading={processing}
                disabled={!topUpForm?.businessId || !topUpForm?.eurosReceived || !topUpForm?.transferReference || !topUpForm?.notes || processing}
                fullWidth
              >
                Conferma Ricarica
              </Button>
            </div>
          </div>
        </div>
      )}

      {showPayoutModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1100 p-6">
          <div className="bg-card rounded-xl p-6 max-w-2xl w-full border border-border shadow-warm-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-foreground">Registra Pagamento</h3>
              <button onClick={() => { setShowPayoutModal(false); setSelectedBusinessForPayout(null); resetPayoutForm(); }}>
                <Icon name="X" size={24} className="text-muted-foreground hover:text-foreground" />
              </button>
            </div>

            <div className="bg-warning/5 border border-warning/20 rounded-lg p-4 mb-6">
              <p className="text-sm font-semibold text-foreground mb-1">{selectedBusinessForPayout?.businessName}</p>
              <p className="text-sm text-muted-foreground">Credito maturato: €{selectedBusinessForPayout?.creditsOwed?.toFixed(2)}</p>
            </div>

            <div className="space-y-4">
              <Input
                type="number"
                label="Importo Pagato (Euro) *"
                placeholder="0.00"
                value={payoutForm?.amountEuro}
                onChange={(e) => setPayoutForm({ ...payoutForm, amountEuro: e?.target?.value })}
                step="0.01"
                min="0"
              />
              
              <Input
                type="date"
                label="Data Pagamento *"
                value={payoutForm?.paymentDate}
                onChange={(e) => setPayoutForm({ ...payoutForm, paymentDate: e?.target?.value })}
              />
              
              <Input
                type="text"
                label="Riferimento Pagamento *"
                placeholder="Es: BON123456789"
                value={payoutForm?.paymentReference}
                onChange={(e) => setPayoutForm({ ...payoutForm, paymentReference: e?.target?.value })}
              />
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Nota *</label>
                <textarea
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  rows="3"
                  placeholder="Inserisci dettagli del pagamento..."
                  value={payoutForm?.notes}
                  onChange={(e) => setPayoutForm({ ...payoutForm, notes: e?.target?.value })}
                />
              </div>
            </div>

            <div className="flex gap-4 mt-6">
              <Button
                variant="outline"
                onClick={() => { setShowPayoutModal(false); setSelectedBusinessForPayout(null); resetPayoutForm(); }}
                fullWidth
              >
                Annulla
              </Button>
              <Button
                variant="default"
                onClick={handlePayoutSubmit}
                loading={processing}
                disabled={!payoutForm?.amountEuro || !payoutForm?.paymentReference || !payoutForm?.notes || processing}
                fullWidth
              >
                Conferma Pagamento
              </Button>
            </div>
          </div>
        </div>
      )}

      {showReversalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1100 p-6">
          <div className="bg-card rounded-xl p-6 max-w-2xl w-full border border-border shadow-warm-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-foreground">Storno Operazione</h3>
              <button onClick={() => { setShowReversalModal(false); setReversalTransaction(null); setReversalReason(''); }}>
                <Icon name="X" size={24} className="text-muted-foreground hover:text-foreground" />
              </button>
            </div>

            <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={20} className="text-destructive mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-foreground mb-1">Attenzione</p>
                  <p className="text-sm text-muted-foreground">
                    Lo storno creerà una transazione inversa per annullare l'operazione. L'operazione originale rimarrà visibile nel ledger per tracciabilità.
                  </p>
                </div>
              </div>
            </div>

            {reversalTransaction && (
              <div className="bg-background rounded-lg p-4 border border-border mb-6">
                <p className="text-sm text-muted-foreground mb-2">Transazione da stornare</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Tipo</p>
                    <p className="text-sm font-semibold text-foreground">{getTransactionTypeLabel(reversalTransaction?.transactionType)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Punti</p>
                    <p className="text-sm font-semibold text-foreground">{reversalTransaction?.pointsAmount?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Valore</p>
                    <p className="text-sm font-semibold text-foreground">€{reversalTransaction?.euroAmount?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Data</p>
                    <p className="text-sm font-semibold text-foreground">
                      {format(new Date(reversalTransaction?.createdAt), 'dd/MM/yyyy HH:mm', { locale: it })}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Motivazione Storno *</label>
              <textarea
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                rows="4"
                placeholder="Inserisci la motivazione dello storno (obbligatoria)..."
                value={reversalReason}
                onChange={(e) => setReversalReason(e?.target?.value)}
              />
            </div>

            <div className="flex gap-4 mt-6">
              <Button
                variant="outline"
                onClick={() => { setShowReversalModal(false); setReversalTransaction(null); setReversalReason(''); }}
                fullWidth
              >
                Annulla
              </Button>
              <Button
                variant="destructive"
                onClick={handleReversalSubmit}
                loading={processing}
                disabled={!reversalReason?.trim() || processing}
                fullWidth
              >
                Conferma Storno
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;